import React, { useEffect, useState, useRef } from "react";
import {
  X,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import {
  revSyncService,
  Reconciliation,
  ReconciliationHistory,
} from "../../services/RevSyncService";
import { useToastContext } from "../../context/ToastContext";

interface ReconciliationModalProps {
  isOpen: boolean;
  onClose: () => void;
  isClosing: boolean;
  isOpening: boolean;
}

const ReconciliationModal: React.FC<ReconciliationModalProps> = ({
  isOpen,
  onClose,
  isClosing,
  isOpening,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToastContext();

  const [reconciliations, setReconciliations] = useState<Reconciliation[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  const loadHistory = async (page: number = currentPage) => {
    setIsLoading(true);
    try {
      const { data } = await revSyncService.getReconciliationHistory(
        page,
        pagination.limit
      );
      setReconciliations(data.reconciliations);
      setPagination(data.pagination);
      setCurrentPage(data.pagination.page);
    } catch (error: any) {
      console.error("Error loading reconciliation history:", error);
      const errorMessage =
        error?.message || "Failed to load reconciliation history.";
      showToast("error", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTriggerReconciliation = async () => {
    setIsTriggering(true);
    try {
      // No mode passed - backend defaults to "auto"
      // Job is queued to SQS, returns immediately (202)
      const { message, userMessage } =
        await revSyncService.triggerReconciliation();
      showToast("success", userMessage || message || "Reconciliation started!");
      // Refresh history after a short delay to see the new reconciliation entry
      setTimeout(() => {
        loadHistory(1);
      }, 1000);
    } catch (error: any) {
      console.error("Error triggering reconciliation:", error);
      const errorMessage =
        error?.message || "Failed to trigger reconciliation.";
      showToast("error", errorMessage);
    } finally {
      setIsTriggering(false);
    }
  };

  const toggleRowExpansion = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getStatusBadge = (status: Reconciliation["status"]) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium";
    switch (status) {
      case "running":
        return (
          <span
            className={`${baseClasses} bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800`}
          >
            <Loader2 className="w-3 h-3 animate-spin mr-1.5" />
            Running
          </span>
        );
      case "completed":
        return (
          <span
            className={`${baseClasses} bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800`}
          >
            Completed
          </span>
        );
      case "failed":
        return (
          <span
            className={`${baseClasses} bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800`}
          >
            Failed
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
  };

  // Load history when modal opens
  useEffect(() => {
    if (isOpen) {
      loadHistory(1);
    }
  }, [isOpen]);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadHistory(newPage);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-md z-50"
        style={{
          opacity: isClosing ? 0 : isOpening ? 0 : 1,
          pointerEvents: isClosing ? "none" : "auto",
          transition: isClosing
            ? "opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
            : "opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
        }}
        onClick={onClose}
      />

      {/* Modal Panel */}
      <div
        ref={modalRef}
        className="fixed inset-y-0 right-0 w-full max-w-4xl bg-background-paper dark:bg-background-paperDark shadow-2xl z-50 overflow-y-auto"
        style={{
          transform: isOpening
            ? "translateX(100%)"
            : isClosing
            ? "translateX(100%)"
            : "translateX(0)",
          opacity: isClosing ? 0 : 1,
          transition: isClosing
            ? "transform 0.2s cubic-bezier(0.4, 0, 1, 1), opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
            : "transform 0.3s cubic-bezier(0, 0, 0.2, 1), opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-divider dark:border-divider-dark sticky top-0 bg-background-paper dark:bg-background-paperDark z-10">
            <div>
              <h2 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark">
                Reconciliation History
              </h2>
              <p className="text-sm text-text-secondary dark:text-text-secondaryDark mt-1">
                Sync Stripe subscriptions with HubSpot contracts
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleTriggerReconciliation()}
                disabled={isTriggering}
                className="px-3 py-1.5 text-xs font-medium text-secondary hover:text-secondary-hover rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 bg-transparent hover:bg-secondary/5"
              >
                {isTriggering ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3.5 h-3.5" />
                    Reconcile Now
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-6">
            {/* About Section */}
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-blue-900 dark:text-blue-300">
                  How Reconciliation Works
                </span>
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Searches for Stripe subscriptions and creates contracts when
                matching HubSpot deals are found. Runs automatically in the
                background or can be triggered manually.
              </p>
            </div>

            {isLoading && reconciliations.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-secondary" />
                <span className="ml-3 text-sm text-text-secondary dark:text-text-secondaryDark">
                  Loading reconciliation history...
                </span>
              </div>
            ) : reconciliations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-full mb-3">
                  <RefreshCw className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                  No reconciliation history yet
                </p>
                <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1">
                  Use "Reconcile Now" above to start syncing Stripe
                  subscriptions with HubSpot
                </p>
              </div>
            ) : (
              <>
                {/* History Table */}
                <div className="bg-background-panel dark:bg-background-panelDark rounded-lg border border-divider dark:border-divider-dark overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto border-collapse">
                      <thead>
                        <tr className="border-b border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-text-primary dark:text-text-primaryDark">
                            Status
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-text-primary dark:text-text-primaryDark">
                            Mode
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-text-primary dark:text-text-primaryDark">
                            Scanned
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-text-primary dark:text-text-primaryDark">
                            Upserted
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-text-primary dark:text-text-primaryDark">
                            Errors
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-text-primary dark:text-text-primaryDark">
                            Started
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-text-primary dark:text-text-primaryDark">
                            Completed
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-text-primary dark:text-text-primaryDark w-10">
                            {""}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {reconciliations.map((reconciliation) => (
                          <React.Fragment key={reconciliation.id}>
                            <tr className="border-b border-divider dark:border-divider-dark hover:bg-background-default dark:hover:bg-background-dark transition-colors">
                              <td className="py-3 px-4">
                                {getStatusBadge(reconciliation.status)}
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-xs text-text-primary dark:text-text-primaryDark capitalize">
                                  {reconciliation.mode}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-xs text-text-primary dark:text-text-primaryDark">
                                  {reconciliation.subscriptionsScanned.toLocaleString()}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-xs text-text-primary dark:text-text-primaryDark">
                                  {reconciliation.contractsUpserted.toLocaleString()}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                {reconciliation.errors.length > 0 ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    {reconciliation.errors.length}
                                  </span>
                                ) : (
                                  <span className="text-xs text-text-secondary dark:text-text-secondaryDark">
                                    —
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-xs text-text-secondary dark:text-text-secondaryDark">
                                  {formatDate(reconciliation.startedAt)}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-xs text-text-secondary dark:text-text-secondaryDark">
                                  {formatDate(reconciliation.completedAt)}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                {reconciliation.errors.length > 0 && (
                                  <button
                                    onClick={() =>
                                      toggleRowExpansion(reconciliation.id)
                                    }
                                    className="p-1 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors"
                                  >
                                    {expandedRows.has(reconciliation.id) ? (
                                      <ChevronUp className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                                    )}
                                  </button>
                                )}
                              </td>
                            </tr>
                            {expandedRows.has(reconciliation.id) &&
                              reconciliation.errors.length > 0 && (
                                <tr className="border-b border-divider dark:border-divider-dark bg-red-50/50 dark:bg-red-900/10">
                                  <td colSpan={8} className="py-4 px-4">
                                    <div className="space-y-2">
                                      <h4 className="text-xs font-semibold text-red-700 dark:text-red-300 mb-2">
                                        Error Details:
                                      </h4>
                                      {reconciliation.errors.map(
                                        (error, index) => (
                                          <div
                                            key={index}
                                            className="text-xs text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800"
                                          >
                                            {error}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-text-secondary dark:text-text-secondaryDark">
                      Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                      {Math.min(
                        pagination.page * pagination.limit,
                        pagination.total
                      )}{" "}
                      of {pagination.total} reconciliations
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1 || isLoading}
                        className="p-2 border border-divider dark:border-divider-dark rounded-lg text-text-secondary dark:text-text-secondaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-text-primary dark:text-text-primaryDark px-3">
                        Page {pagination.page} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={
                          pagination.page === pagination.totalPages || isLoading
                        }
                        className="p-2 border border-divider dark:border-divider-dark rounded-lg text-text-secondary dark:text-text-secondaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ReconciliationModal;
